import { useCallback, useEffect, useId, useRef, useState } from "react";
import { ApiError, api } from "../api";
import type { FeatureAccess } from "../features";
import { profileDraftFingerprint } from "../profile-draft";
import { occupations, type ProfileDetails, purposes } from "../reference-data";
import type { Location, Me } from "../types";
import { PhotoManagementContent } from "./PhotoManagement";
import { ProfileExtras } from "./ProfileExtras";
import { ProfileVideoContent } from "./ProfileVideo";
import { ChoiceList, MenuRow, PageDialog } from "./ReferenceUI";

export type ProfileEditorTab = "info" | "photos" | "video";
export function ProfileEditor({
  me,
  onClose,
  onRefresh,
  initialTab = "info",
}: {
  me: Me;
  onClose(): void;
  onRefresh(): Promise<void>;
  initialTab?: ProfileEditorTab;
}) {
  const initialIdentity = useRef({ name: me.profile?.displayName ?? "", bio: me.profile?.bio ?? "" });
  const [tab, setTab] = useState<ProfileEditorTab>(initialTab);
  const [mediaBusy, setMediaBusy] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [savedFingerprint, setSavedFingerprint] = useState<string | null>(null);
  const tabId = useId();
  const [premium, setPremium] = useState(false);
  const [access, setAccess] = useState<FeatureAccess | null>(null);
  const [value, setValue] = useState<ProfileDetails | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [name, setName] = useState(me.profile?.displayName ?? "");
  const [bio, setBio] = useState(me.profile?.bio ?? "");
  const [error, setError] = useState("");
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState<"purpose" | "occupation" | "locationId" | null>(null);
  const load = useCallback(async () => {
    setError("");
    try {
      const [details, cities, rights] = await Promise.all([
        api<ProfileDetails>("/v1/me/profile-details"),
        api<{ locations: Location[] }>("/v1/locations"),
        api<FeatureAccess>("/v1/me/features").catch(() => null),
      ]);
      setValue(details);
      setSavedFingerprint(profileDraftFingerprint(initialIdentity.current.name, initialIdentity.current.bio, details));
      setAccess(rights);
      setLocations(cities.locations);
    } catch {
      setError("Не удалось загрузить профиль.");
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const dirty = value && savedFingerprint !== null && profileDraftFingerprint(name, bio, value) !== savedFingerprint;
  const requestClose = () => {
    if (dirty) setConfirmClose(true);
    else onClose();
  };
  const save = async () => {
    if (!value || busy) return;
    setBusy(true);
    setError("");
    setRefreshFailed(false);
    const savedName = name.trim(),
      savedBio = bio.trim();
    try {
      await api("/v1/me/profile-details", {
        method: "PUT",
        body: JSON.stringify({ ...value, displayName: savedName, bio: savedBio || null }),
      });
      setName(savedName);
      setBio(savedBio);
      setSavedFingerprint(profileDraftFingerprint(savedName, savedBio, value));
      try {
        await onRefresh();
        onClose();
      } catch {
        setRefreshFailed(true);
        setError("Информация сохранена. Не удалось обновить экран профиля.");
      }
    } catch (error) {
      setError(
        error instanceof ApiError && error.code === "premium_required"
          ? "Для включения этой настройки нужен действующий Premium. Остальные изменения ещё не сохранены."
          : "Не удалось сохранить профиль. Проверьте данные и повторите попытку.",
      );
    } finally {
      setBusy(false);
    }
  };
  const refreshProfile = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await onRefresh();
      setRefreshFailed(false);
      if (!dirty) onClose();
    } catch {
      setError("Информация сохранена. Не удалось обновить экран профиля.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <PageDialog title="Редактирование профиля" onClose={requestClose} busy={busy || mediaBusy}>
      <div className="reference-tabs" role="tablist" aria-label="Разделы редактора профиля">
        {(
          [
            ["info", "Информация"],
            ["photos", "Фотографии"],
            ["video", "Видео"],
          ] as const
        ).map(([key, label], index) => (
          <button
            type="button"
            key={key}
            id={`${tabId}-${key}-tab`}
            role="tab"
            aria-selected={tab === key}
            aria-controls={`${tabId}-panel`}
            tabIndex={tab === key ? 0 : -1}
            className={tab === key ? "active" : ""}
            disabled={busy || mediaBusy}
            onClick={() => setTab(key)}
            onKeyDown={(event) => {
              if (busy || mediaBusy) return;
              const keys = ["info", "photos", "video"] as const;
              const next =
                event.key === "ArrowRight"
                  ? (index + 1) % 3
                  : event.key === "ArrowLeft"
                    ? (index + 2) % 3
                    : event.key === "Home"
                      ? 0
                      : event.key === "End"
                        ? 2
                        : null;
              if (next === null) return;
              event.preventDefault();
              const key = keys[next];
              if (key) {
                setTab(key);
                document.getElementById(`${tabId}-${key}-tab`)?.focus();
              }
            }}
          >
            {label}
          </button>
        ))}
      </div>
      {tab !== "info" && dirty && (
        <p className="reference-note" role="status">
          Изменения информации не сохранены. Они останутся в редакторе при переключении вкладок.
        </p>
      )}
      <div
        className="profile-editor-panel"
        id={`${tabId}-panel`}
        role="tabpanel"
        aria-labelledby={`${tabId}-${tab}-tab`}
      >
        {tab === "photos" ? (
          <PhotoManagementContent photos={me.profile?.photos ?? []} onRefresh={onRefresh} onBusyChange={setMediaBusy} />
        ) : tab === "video" ? (
          <ProfileVideoContent onBusyChange={setMediaBusy} />
        ) : !value ? (
          <div role="status">
            {error || "Загружаем профиль…"}
            {error && (
              <button type="button" onClick={() => void load()}>
                Повторить
              </button>
            )}
          </div>
        ) : (
          <fieldset className="profile-information-fields" aria-label="Информация анкеты" disabled={busy}>
            <label className="reference-field">
              ИМЯ
              <input
                className="reference-input"
                value={name}
                maxLength={50}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="reference-field">
              ОБО МНЕ
              <textarea
                className="reference-input"
                value={bio}
                rows={5}
                maxLength={500}
                onChange={(e) => setBio(e.target.value)}
              />
              <small className="reference-counter">{bio.length}/500</small>
            </label>
            <p className="reference-caption">МЕСТОПОЛОЖЕНИЕ</p>
            <div className="reference-group">
              <MenuRow
                label="Город"
                value={locations.find((l) => l.id === value.locationId)?.name ?? me.profile?.location.name}
                onClick={() => setPicker("locationId")}
              />
            </div>
            <label className="reference-field">
              Станция или район
              <input
                className="reference-input"
                maxLength={80}
                value={value.station ?? ""}
                placeholder="Указать"
                onChange={(e) => setValue({ ...value, station: e.target.value || null })}
              />
            </label>
            <p className="reference-caption">ДОПОЛНИТЕЛЬНО</p>
            <div className="reference-group">
              <MenuRow
                icon="♡"
                label="Я хочу"
                value={purposes.find((p) => p.value === value.purpose)?.label ?? "Указать"}
                onClick={() => setPicker("purpose")}
              />
              <MenuRow
                icon="▣"
                label="Деятельность"
                value={value.occupation ?? "Указать"}
                onClick={() => setPicker("occupation")}
              />
              <label className="reference-row">
                <span className="reference-row-icon">↟</span>
                <span className="reference-row-label">Рост</span>
                <input
                  type="number"
                  aria-label="Рост в сантиметрах"
                  min={140}
                  max={210}
                  placeholder="см"
                  value={value.heightCm ?? ""}
                  onChange={(e) =>
                    setValue({ ...value, heightCm: e.target.value === "" ? null : Number(e.target.value) })
                  }
                />
              </label>
            </div>
            <p className="reference-caption">СОЦИАЛЬНЫЕ СЕТИ</p>
            <label className="reference-field">
              IG
              <input
                className="reference-input"
                placeholder="Имя пользователя без @"
                value={value.socialUsername ?? ""}
                maxLength={30}
                onChange={(e) => setValue({ ...value, socialUsername: e.target.value.replace(/^@/, "") || null })}
              />
            </label>
            <p className="reference-caption">ПРИВАТНОСТЬ</p>
            <div className="reference-group">
              {(
                [
                  ["hideAge", "hide_age", "Скрыть мой возраст"],
                  ["hideOnline", "hide_online", "Скрыть статус онлайн"],
                ] as const
              ).map(([key, feature, label]) => (
                <label className="reference-row" key={key}>
                  <span className="reference-row-label">
                    {label}
                    {!access?.features?.[feature] && !value[key] ? " · Premium" : ""}
                  </span>
                  <input
                    type="checkbox"
                    className="reference-switch"
                    aria-label={label}
                    checked={value[key] ?? false}
                    onChange={(event) => {
                      if (event.target.checked && !access?.features?.[feature]) {
                        setPremium(true);
                        return;
                      }
                      setValue({ ...value, [key]: event.target.checked });
                    }}
                  />
                </label>
              ))}
            </div>
            {error && (
              <p className="reference-error" role="alert">
                {error}
              </p>
            )}
            {refreshFailed && (
              <button type="button" className="reference-row" disabled={busy} onClick={() => void refreshProfile()}>
                Обновить профиль
              </button>
            )}
            <button
              type="button"
              className="reference-primary reference-save"
              disabled={
                busy ||
                !dirty ||
                name.trim().length < 2 ||
                (value.heightCm !== null && (value.heightCm < 140 || value.heightCm > 210)) ||
                (value.socialUsername !== null && !/^[a-zA-Z0-9._]{1,30}$/.test(value.socialUsername))
              }
              onClick={() => void save()}
            >
              {busy ? "Сохраняем…" : "Сохранить"}
            </button>
          </fieldset>
        )}
      </div>
      {picker && value && (
        <ChoiceList
          title={picker === "purpose" ? "Я хочу" : picker === "occupation" ? "Деятельность" : "Город"}
          options={
            picker === "purpose"
              ? [...purposes]
              : picker === "occupation"
                ? occupations.map((o) => ({ value: o, label: o }))
                : locations.map((l) => ({ value: l.id, label: l.name }))
          }
          value={value[picker]}
          searchable={picker !== "purpose"}
          onClose={() => setPicker(null)}
          onSelect={(v) => {
            if (picker === "locationId" && !v) return;
            setValue({ ...value, [picker]: v });
            setPicker(null);
          }}
        />
      )}

      {confirmClose && (
        <PageDialog title="Выйти без сохранения?" onClose={() => setConfirmClose(false)}>
          <p>Несохранённые изменения информации будут потеряны. Фотографии и видео сохраняются сразу после загрузки.</p>
          <button type="button" className="reference-row" onClick={() => setConfirmClose(false)}>
            Продолжить редактирование
          </button>
          <button type="button" className="reference-row" onClick={onClose}>
            Выйти без сохранения
          </button>
        </PageDialog>
      )}
      {premium && <ProfileExtras page="premium" onClose={() => setPremium(false)} />}
    </PageDialog>
  );
}
